import { useState, useEffect, useRef } from 'react';

/**
 * Hook to subscribe to real-time job progress updates via SSE
 * @param {string} jobId - Job UUID
 * @param {string} jobType - 'pdf' or 'email'
 * @param {boolean} enabled - Whether to connect to SSE
 * @returns {object} Progress data
 */
export const useJobProgress = (jobId, jobType, enabled = true) => {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    if (!enabled || !jobId) return;

    // Construct SSE endpoint URL
    // Note: Don't use api.js wrapper for SSE, construct URL directly
    // REACT_APP_API_URL already includes /api (e.g., http://localhost:3001/api)
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    
    // Endpoint without /api prefix (baseUrl already has it)
    const endpoint = jobType === 'email' 
      ? `/jobs/email/${jobId}/progress`
      : `/jobs/${jobId}/progress`;
    
    const url = `${baseUrl}${endpoint}`;
    
    // Get auth token for SSE connection
    const token = localStorage.getItem('token');
    
    console.log(`📡 Connecting to SSE: ${url}`);

    // EventSource doesn't support custom headers, so we need to pass token via URL
    const urlWithAuth = token ? `${url}?token=${token}` : url;

    // Create EventSource connection
    const eventSource = new EventSource(urlWithAuth, {
      withCredentials: true // Send cookies for auth
    });

    eventSource.onopen = () => {
      console.log(`📡 SSE connected for ${jobType} job ${jobId}`);
      setError(null);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log(`📡 SSE update for ${jobType} job ${jobId}:`, data);
        
        if (data.type === 'connected') {
          console.log('📡 SSE connection confirmed');
        } else if (data.type === 'finished') {
          console.log('📡 SSE job finished, closing connection');
          eventSource.close();
        } else {
          // Update progress state
          setProgress(data);
        }
      } catch (err) {
        console.error('📡 SSE parse error:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error(`📡 SSE error for ${jobType} job ${jobId}:`, err);
      setError('Connection error');
      eventSource.close();
    };

    eventSourceRef.current = eventSource;

    // Cleanup on unmount
    return () => {
      console.log(`📡 Cleaning up SSE connection for ${jobType} job ${jobId}`);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [jobId, jobType, enabled]);

  return { progress, error };
};

